import { reactive, watch, ref, toRefs, onMounted, onUnmounted, Ref, getCurrentInstance } from 'vue';
import webPreset from './lib/web-preset';
import SWRVCache from './cache';
import type {
    Config,
    ResponsiveConfig,
    NotResponsiveConfig,
    IKey,
    Status,
    Response,
    ResponsiveResponse,
    NotResponsiveResponse,
    FetcherFn,
    revalidateOptions
} from './types';
import LocalStoargeCache from './cache/adapters/localStorage';

interface StateRef<Data, Error> {
    data: Data | undefined;
    error: Error | undefined;
    status: Status;
    isValidating: boolean;
    _key: any;
    _fetcher: FetcherFn<any> | undefined;
}

const DATA_CACHE = new SWRVCache<Omit<ResponsiveResponse, 'mutate'>>();
const DATA_CACHE_LOCAL = new LocalStoargeCache('ptmind-swr');
const CONCURRENT_REF = new Map();
const CONCURRENT_PROMISES = new Map();

const defaultResponsiveConfig = ({
    cacheLocal: false,
    refreshInterval: 0,
    ttl: 0,
    dedupingInterval: 2000,
    revalidateOnFocus: false,
    revalidateDebounce: 0,
    shouldRetryOnError: true,
    errorRetryInterval: 5000,
    errorRetryCount: 5,
    responsive: true,
    readonly: false,
    immediateRevalidate: true,
    isOnline: webPreset.isOnline,
    isDocumentVisible: webPreset.isDocumentVisible
} as unknown) as Required<ResponsiveConfig>;

const defaultNotResponsiveConfig = ({
    shouldRetryOnError: true,
    errorRetryInterval: 5000,
    errorRetryCount: 5,
    responsive: false,
    fetcher: webPreset.fetcher,
    isOnline: webPreset.isOnline,
    isDocumentVisible: webPreset.isDocumentVisible
} as unknown) as Required<NotResponsiveConfig>;

/**
 * Cache the refs for later revalidation
 */
function setRefCache(key: string, State: StateRef<any, any>) {
    const refs = CONCURRENT_REF.get(key);
    if (refs) {
        refs.push(State);
    } else {
        CONCURRENT_REF.set(key, [State]);
    }
}

// function deleteRefCache(key: string, TargetState: StateRef<any, any>) {
//     const refs: Array<StateRef<any, any>> = CONCURRENT_REF.get(key);
//     if (!refs) return;
//     const targetIndex = refs.findIndex(State => State === TargetState);
//     if (targetIndex >= 0) refs.splice(targetIndex, 0);
// }

function onErrorRetry(
    revalidate: (opts?: revalidateOptions) => void,
    errorRetryCount: number,
    config: Required<Config>
): void {
    if (!config.isDocumentVisible()) {
        return;
    }

    if (config.errorRetryCount && errorRetryCount > config.errorRetryCount) {
        return;
    }

    const count = Math.min(errorRetryCount || 0, config?.errorRetryCount ?? 0);
    const timeout = count * (config?.errorRetryInterval ?? 0);
    setTimeout(() => {
        revalidate({ errorRetryCount: count + 1, shouldRetryOnError: true });
    }, timeout);
}

async function onErrorRetryNotResponsive() {}

/**
 * Main mutation function for receiving data from promises to change state and
 * set data cache
 */
const mutate = async <Data>(
    key: string,
    res: Promise<Data> | Data,
    cache = DATA_CACHE,
    ttl = defaultResponsiveConfig.ttl
) => {
    let data, error;

    if (isPromise(res)) {
        try {
            data = await res;
        } catch (err) {
            error = err;
        }
    } else {
        data = res;
    }

    const newData = { data, error, isValidating: false };
    if (typeof data !== 'undefined') {
        cache.set(key, newData, ttl);
    }

    /**
     * Revalidate all swrv instances with new data
     */
    const stateRefs: Array<StateRef<any, any>> = CONCURRENT_REF.get(key);
    if (stateRefs?.length) {
        stateRefs.forEach(state => {
            if (typeof newData.data !== 'undefined') {
                state.data = newData.data;
            }
            state.error = newData.error;
            state.isValidating = newData.isValidating;
        });
    }

    return newData;
};

/* Stale-While-Revalidate hook to handle fetching, caching, validation, and more... */
function useSWR<Data = any, Error = any>(key: IKey): ResponsiveResponse<Data, Error>;
function useSWR<Data = any, Error = any>(
    key: IKey,
    fetcherFn?: Function,
    config?: ResponsiveConfig
): ResponsiveResponse<Data, Error>;
function useSWR<Data = any, Error = any, Variables = any>(
    fetcherFn: Function,
    config: NotResponsiveConfig
): NotResponsiveResponse<Data, Error, Variables>;
function useSWR<Data = any, Error = any, Variables = any>(...args: any[]): Response<Data, Error, Variables> {
    let res: Response = undefined as any;
    let key: Ref<IKey> | Function = undefined as any;
    let fetcherFn: FetcherFn<Data> = (() => {}) as any;
    let config: Required<Config> = { ...defaultResponsiveConfig };
    let ttl: number;
    let unmounted = false;

    if (args.length == 2 && (args[1] as NotResponsiveConfig)?.responsive === false) {
        fetcherFn = args[0];
        config = {
            ...defaultNotResponsiveConfig,
            ...args[1]
        };
    } else {
        if (args.length >= 1) {
            key = typeof args[0] === 'function' ? args[0] : ref(args[0]);
        }
        if (args.length >= 2) {
            fetcherFn = args[1];
        }
        if (args.length >= 3) {
            config = {
                ...defaultResponsiveConfig,
                ...args[2]
            };
        }
    }

    let stateRef = reactive({
        data: undefined,
        error: undefined,
        status: config.responsive ? 'pending' : 'waiting',
        isValidating: true,
        _key: null,
        _fetcher: undefined
    }) as StateRef<Data, Error>;

    if (config.responsive) {
        if (config.cacheLocal) config._cache = (DATA_CACHE_LOCAL as unknown) as SWRVCache<any>;
        else config._cache = DATA_CACHE;

        ttl = (config as ResponsiveConfig).ttl ?? 0;

        /**
         * Revalidate the cache, mutate data
         */
        const revalidate = async (opts?: revalidateOptions) => {
            if (config.responsive === false) return;
            const isFirstFetch = stateRef.data === undefined;
            if (!stateRef._key) return;

            const cacheItem = config._cache.get(stateRef._key);
            let cacheData: StateRef<Data, Error> | undefined = cacheItem && cacheItem.data;

            // stateRef.isValidating = true;
            if (isFirstFetch && cacheData) {
                stateRef.data = cacheData.data;
                stateRef.error = cacheData.error;
            }

            if (
                !stateRef._fetcher ||
                (!config.isDocumentVisible() && !isFirstFetch) ||
                (opts?.forceRevalidate !== undefined && !opts?.forceRevalidate)
            ) {
                stateRef.status = 'success';
                return;
            }

            // Dedupe items that were created in the last interval #76
            if (cacheItem) {
                const shouldRevalidate = Boolean(
                    Date.now() - cacheItem.createdAt >= (config.dedupingInterval ?? NaN) || opts?.forceRevalidate
                );

                if (!shouldRevalidate) {
                    stateRef.status = 'success';
                    return;
                }
            }

            const trigger = async () => {
                if (!stateRef._fetcher || !stateRef._key) return;

                const currentPromise = CONCURRENT_PROMISES.get(stateRef._key);
                stateRef.isValidating = true;
                stateRef.status =
                    stateRef.error == undefined
                        ? stateRef.data
                            ? 'validating'
                            : 'pending'
                        : stateRef.data
                        ? 'stale_if_error_retry'
                        : 'err_retry';

                if (!currentPromise) {
                    const newPromise = stateRef._fetcher();
                    CONCURRENT_PROMISES.set(stateRef._key, newPromise);
                    await mutate(stateRef._key, newPromise, config._cache, ttl);
                } else {
                    await mutate(stateRef._key, currentPromise, config._cache, ttl);
                }

                stateRef.isValidating = false;

                CONCURRENT_PROMISES.delete(stateRef._key);
                if (stateRef.error !== undefined) {
                    const shouldRetryOnError =
                        (stateRef.error as any)?.httpStatus !== 401 &&
                        config.shouldRetryOnError &&
                        (opts ? opts.shouldRetryOnError : true);

                    if (shouldRetryOnError) onErrorRetry(revalidate, opts?.errorRetryCount ?? 1, config);
                    else {
                        if (cacheData) stateRef.status = 'stale_if_error';
                        else stateRef.status = 'error';
                    }
                } else stateRef.status = 'success';
            };

            if (cacheData && config.revalidateDebounce) {
                await setTimeout(async () => {
                    if (!unmounted) {
                        await trigger();
                    }
                }, config.revalidateDebounce);
            } else {
                await trigger();
            }
        };

        const revalidateCall = async () => revalidate({ shouldRetryOnError: false });
        let timer: number | null = null;
        /**
         * Setup polling
         */
        onMounted(() => {
            if (!config.responsive) return;

            const tick = async () => {
                // component might un-mount during revalidate, so do not set a new timeout
                // if this is the case, but continue to revalidate since promises can't
                // be cancelled and new hook instances might rely on promise/data cache or
                // from pre-fetch
                if (!stateRef.error && config.isOnline()) {
                    // if API request errored, we stop polling in this round
                    // and let the error retry function handle it
                    await revalidate();
                } else {
                    if (timer) {
                        clearTimeout(timer);
                    }
                }

                if (config.refreshInterval && !unmounted) {
                    timer = setTimeout(tick, config.refreshInterval);
                }
            };

            if (config.refreshInterval) {
                timer = setTimeout(tick, config.refreshInterval);
            }
            if (config.revalidateOnFocus) {
                document.addEventListener('visibilitychange', revalidateCall, false);
                window.addEventListener('focus', revalidateCall, false);
            }
        });

        /**
         * Teardown
         */
        onUnmounted(() => {
            unmounted = true;
            if (timer) {
                clearTimeout(timer);
            }
            if (config.revalidateOnFocus) {
                document.removeEventListener('visibilitychange', revalidateCall, false);
                window.removeEventListener('focus', revalidateCall, false);
            }
        });

        try {
            watch(
                key,
                val => {
                    stateRef._key = val;
                    setRefCache(stateRef._key, stateRef);

                    if (stateRef._key) revalidate();
                },
                {
                    immediate: true
                }
            );

            config?.immediateRevalidate &&
                watch(
                    () => fetcherFn,
                    val => {
                        stateRef._fetcher = val as FetcherFn<any>;

                        if (stateRef._key && stateRef._fetcher) revalidate();
                    },
                    {
                        immediate: true
                    }
                );
        } catch (err) {
            console.log(err);
            // do nothing
        }

        res = {
            ...toRefs(stateRef),
            mutate: (data: Data) => mutate(stateRef._key, data, config._cache, config.ttl),
            revalidate,
            preFecth: revalidateCall
        } as ResponsiveResponse;

        return res;
    } else {
        const fetch = async (variables: Variables) => {
            if (!fetcherFn) return;
            stateRef.isValidating = true;
            // stateRef.status = stateRef.error == undefined ? 'pending' : 'err_retry';
            stateRef.status = 'validating';

            try {
                stateRef.data = await fetcherFn?.(variables);
                stateRef.error = undefined;
            } catch (err) {
                stateRef.error = err;
            }
            stateRef.isValidating = false;

            if (stateRef.error !== undefined) {
                stateRef.status = 'error';
                // const shouldRetryOnError = config.shouldRetryOnError;
            } else stateRef.status = 'success';

            return stateRef.data;
        };

        res = {
            ...toRefs(stateRef),
            fetch
        } as NotResponsiveResponse;
        return res;
    }
}

function isPromise<T>(p: any): p is Promise<T> {
    return p !== null && typeof p === 'object' && typeof p.then === 'function';
}

export { mutate };
export default useSWR;
